import { createPortal } from 'react-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  FolderOpen,
  ImageOff,
  Images,
  Loader2,
  Lock,
  Pencil,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
  Wand2,
  X,
  ZoomIn,
} from 'lucide-react';
import toast from '../../../../components/Toast';
import type {
  TrinityCardItem,
  TrinityCardsGenerateInput,
  TrinityCardSlot,
  TrinityReferenceAssetResolution,
  TrinityCardsTask,
} from '../../services/directorApi';
import { directorApi } from '../../services/directorApi';
import { withAuthToken } from '../../../../utils/storageUrl';
import { FrameLightbox, type FrameLightboxItem } from './FrameLightbox';
import { api as imageApi, type ModelConfig } from '../../../../services/api';
import { TrinityPromptGenerateDialog } from './TrinityPromptGenerateDialog';
import { TrinityImageGenerateDialog } from './TrinityImageGenerateDialog';
import {
  ImportFromLibraryDialog,
  type LibraryImportTarget,
} from '../library/ImportFromLibraryDialog';

interface InjectedAssetHints {
  characters: string[];
  props: string[];
  scene: string | null;
}

interface Props {
  open: boolean;
  shotId: string;
  cards: TrinityCardItem[];
  busyKey: string | null;
  task: TrinityCardsTask | null;
  aiCost: number | null;
  aiEnabled: boolean;
  videoActive: boolean;
  completedCount: number;
  currentCredits: number;
  injectedAssets: InjectedAssetHints;
  referenceAssets: TrinityReferenceAssetResolution | null;
  referenceAssetsLoading: boolean;
  onClose: () => void;
  onRefresh: () => void;
  onGeneratePrompt: (
    slot: TrinityCardSlot,
    config?: Pick<TrinityCardsGenerateInput, 'modelId' | 'quality'>
  ) => void;
  onGenerateImage: (
    slot: TrinityCardSlot,
    config?: Pick<TrinityCardsGenerateInput, 'modelId' | 'quality'>
  ) => void;
  onSavePrompt: (slot: TrinityCardSlot, prompt: string) => Promise<void>;
  onLockCandidate: (slot: TrinityCardSlot, imageJobId: string) => void;
  onDeleteCandidate: (slot: TrinityCardSlot, imageJobId: string) => void;
}

const IN_PROGRESS_STATUSES = new Set<TrinityCardItem['status']>([
  'waiting_dependency',
  'pending',
  'submitting',
  'processing',
]);

function statusTone(status: TrinityCardItem['status']) {
  switch (status) {
    case 'draft':
      return 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-100';
    case 'waiting_dependency':
      return 'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-100';
    case 'completed':
      return 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-100';
    case 'failed':
      return 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-100';
    case 'pending':
    case 'submitting':
    case 'processing':
      return 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-100';
    default:
      return 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200';
  }
}

function statusLabel(status: TrinityCardItem['status']) {
  switch (status) {
    case 'draft':
      return '文案就绪';
    case 'waiting_dependency':
      return '等待依赖';
    case 'completed':
      return '已完成';
    case 'failed':
      return '失败';
    case 'pending':
      return '排队中';
    case 'submitting':
      return '提交中';
    case 'processing':
      return '生成中';
    default:
      return '待生成';
  }
}

function missingReasonLabel(reason: 'asset_not_found' | 'missing_image' | 'image_not_completed') {
  if (reason === 'asset_not_found') return '资产不存在';
  if (reason === 'missing_image') return '未配置参考图';
  return '参考图未完成';
}

function getCardBySlot(cards: TrinityCardItem[], slot: TrinityCardSlot) {
  return cards.find((c) => c.slot === slot);
}

function getUpstreamMissing(
  cards: TrinityCardItem[],
  slot: TrinityCardSlot
): string[] {
  if (slot === 'role') return [];
  if (slot === 'story') {
    const role = getCardBySlot(cards, 'role');
    return role?.prompt ? [] : ['角色卡'];
  }
  const missing: string[] = [];
  const role = getCardBySlot(cards, 'role');
  const story = getCardBySlot(cards, 'story');
  if (!role?.prompt) missing.push('角色卡');
  if (!story?.prompt) missing.push('故事卡');
  return missing;
}

export function ShotTrinityCardsDialog({
  open,
  shotId,
  cards,
  busyKey,
  task,
  aiCost,
  aiEnabled,
  videoActive,
  completedCount,
  currentCredits,
  injectedAssets,
  referenceAssets,
  referenceAssetsLoading,
  onClose,
  onRefresh,
  onGeneratePrompt,
  onGenerateImage,
  onSavePrompt,
  onLockCandidate,
  onDeleteCandidate,
}: Props) {
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [candidatePreviewSlot, setCandidatePreviewSlot] = useState<TrinityCardSlot | null>(null);
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [editingSlot, setEditingSlot] = useState<TrinityCardSlot | null>(null);
  const [editingPrompt, setEditingPrompt] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);

  const [promptDialogSlot, setPromptDialogSlot] = useState<TrinityCardSlot | null>(null);
  const [imageDialogSlot, setImageDialogSlot] = useState<TrinityCardSlot | null>(null);
  const [imagePreview, setImagePreview] = useState<{
    referenceCount: number;
    estimatedCredits: number;
  } | null>(null);
  const [libraryImportSlot, setLibraryImportSlot] = useState<TrinityCardSlot | null>(null);
  const [expandedSlots, setExpandedSlots] = useState<Set<TrinityCardSlot>>(new Set());
  const [candidatePreviewId, setCandidatePreviewId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<{
    type: 'lock' | 'delete';
    slot: TrinityCardSlot;
    imageJobId: string;
  } | null>(null);

  const taskRunning = task?.status === 'pending' || task?.status === 'running';
  const injectedCharacterCount = injectedAssets.characters.length;
  const roleRuleHint =
    injectedCharacterCount >= 3
      ? '当前注入了 3 个及以上角色；角色卡会优先稳定前 2 个主角色，其余角色仅作辅助同框信息。'
      : injectedCharacterCount === 2
        ? '当前注入了 2 个角色；角色卡会按双角色关系卡生成，两个角色都会作为主体出现。'
        : injectedCharacterCount === 1
          ? '当前注入了 1 个角色；角色卡会按单角色身份卡生成。'
          : null;
  const lightboxItems = useMemo<FrameLightboxItem[]>(
    () =>
      cards
        .filter((card) => !!(card.imageUrl ?? card.thumbnailUrl))
        .map((card) => ({
          id: card.slot,
          url: withAuthToken(card.imageUrl ?? card.thumbnailUrl ?? ''),
          label: card.label,
        })),
    [cards]
  );

  /** 候选图灯箱项（按 slot 分组） */
  const candidateLightboxItems = useMemo(() => {
    const map = new Map<TrinityCardSlot, FrameLightboxItem[]>();
    for (const card of cards) {
      if (!card.candidates?.length) continue;
      const items: FrameLightboxItem[] = card.candidates
        .filter((c) => c.status === 'completed' && c.imageUrl)
        .map((c, i) => ({
          id: c.imageJobId,
          url: withAuthToken(c.imageUrl ?? ''),
          label: `候选 ${i + 1}`,
        }));
      if (items.length > 0) map.set(card.slot, items);
    }
    return map;
  }, [cards]);

  useEffect(() => {
    if (!open) return;
    imageApi
      .getModels()
      .then((res) => setModels(res.data))
      .catch(() => setModels([]));
  }, [open]);

  const handleClickGeneratePrompt = useCallback(
    (slot: TrinityCardSlot) => {
      if (!aiEnabled) {
        toast.error('当前 AI 融合未开放，请联系管理员');
        return;
      }
      // 检查上游依赖（仅提示，不阻断）
      const missing = getUpstreamMissing(cards, slot);
      if (missing.length > 0) {
        toast(
          `${missing.join('、')}文案为空，${getCardBySlot(cards, slot)?.label}生成效果可能不理想，建议先完成上游文案`,
          { duration: 5000 }
        );
      }
      setPromptDialogSlot(slot);
    },
    [aiEnabled, cards]
  );

  const handleConfirmPrompt = useCallback(() => {
    if (promptDialogSlot) {
      onGeneratePrompt(promptDialogSlot);
    }
  }, [promptDialogSlot, onGeneratePrompt]);

  const handleSavePromptFromDialog = useCallback(
    async (prompt: string) => {
      if (promptDialogSlot) {
        await onSavePrompt(promptDialogSlot, prompt);
        setPromptDialogSlot(null);
      }
    },
    [promptDialogSlot, onSavePrompt]
  );

  const handleClickGenerateImage = useCallback(
    async (slot: TrinityCardSlot) => {
      const card = getCardBySlot(cards, slot);
      if (!card?.prompt) {
        toast.error('请先生成文案，再生成图片');
        return;
      }
      // 检查上游依赖
      const missing = getUpstreamMissing(cards, slot);
      if (missing.length > 0) {
        toast.error(`请先生成${missing.join('和')}文案`);
        return;
      }
      try {
        const preview = await directorApi.previewGenerateSingleTrinityCardImage(shotId, slot);
        setImagePreview({
          referenceCount: preview.referenceCount,
          estimatedCredits: preview.estimatedCredits,
        });
        setImageDialogSlot(slot);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : '获取生成预览失败');
      }
    },
    [cards, shotId]
  );

  const handleConfirmImage = useCallback(
    (config: { modelId: string; quality: 'low' | 'medium' | 'high' }) => {
      if (imageDialogSlot) {
        onGenerateImage(imageDialogSlot, config);
        setImageDialogSlot(null);
        setImagePreview(null);
      }
    },
    [imageDialogSlot, onGenerateImage]
  );


  const promptDialogCard = promptDialogSlot
    ? getCardBySlot(cards, promptDialogSlot)
    : undefined;
  const imageDialogCard = imageDialogSlot
    ? getCardBySlot(cards, imageDialogSlot)
    : undefined;

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <button
        type="button"
        aria-label="关闭"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />
      <div
        className="relative flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl shadow-slate-950/30"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative overflow-hidden bg-gradient-to-br from-violet-950 via-violet-900 to-fuchsia-900 p-4 text-white">
          <div className="absolute right-[-56px] top-[-48px] h-40 w-40 rounded-full bg-fuchsia-400/20 blur-3xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-medium text-violet-100">
                <Sparkles className="h-3 w-3 text-fuchsia-200" />
                三合一参考图
              </div>
              <h2 className="text-lg font-semibold leading-tight">管理三合一参考图</h2>
              <p className="mt-1 text-xs leading-5 text-violet-100/90">
                系统会按 角色卡 - 故事卡 - 视频卡 的依赖链逐步生成，视频激活时固定使用这 3 张图作为参考。
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full bg-white/10 p-1.5 text-white transition hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-slate-50 p-4">
          {/* 顶部状态栏 */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-200">
              已完成 {completedCount}/3
            </span>
            {typeof aiCost === 'number' && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
                AI 融合 {aiCost} 积分/次
              </span>
            )}
            {!aiEnabled && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700">
                <AlertTriangle className="h-3 w-3" />
                当前未开放 AI 融合
              </span>
            )}
            {videoActive && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
                视频已激活，修改卡片前请先取消或重激活
              </span>
            )}
            {roleRuleHint && (
              <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] text-sky-700 ring-1 ring-sky-200">
                {roleRuleHint}
              </span>
            )}
            <button
              type="button"
              onClick={onRefresh}
              disabled={!!busyKey}
              className="ml-auto inline-flex items-center gap-1.5 rounded-2xl border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-violet-200 hover:text-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              刷新状态
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
            {cards.map((card) => {
              const thumbUrl = card.thumbnailUrl ?? card.imageUrl ?? null;
              const inProgress = IN_PROGRESS_STATUSES.has(card.status);
              const hasImage = !!thumbUrl;
              const isPromptBusy = busyKey === `prompt:${card.slot}`;
              const isImageBusy = busyKey === `image:${card.slot}`;
              const candidates = card.candidates ?? [];

              return (
                <div
                  key={card.slot}
                  className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
                >
                  <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{card.label}</div>
                      <div className="text-[11px] text-slate-400">
                        {card.slot === 'role'
                          ? '角色形象'
                          : card.slot === 'story'
                            ? '情节瞬间'
                            : '视频质感'}
                      </div>
                    </div>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusTone(card.status)}`}
                    >
                      {card.status === 'completed' && <CheckCircle2 className="mr-0.5 inline h-3 w-3" />}
                      {statusLabel(card.status)}
                    </span>
                  </div>

                  <div className={`group relative aspect-video bg-slate-100 ${
                    hasImage && card.status === 'completed'
                      ? 'ring-2 ring-violet-500 ring-offset-2 ring-offset-white rounded-lg'
                      : ''
                  }`}>
                    {hasImage && card.status === 'completed' && (
                      <div className="absolute right-1.5 top-1.5 z-10 rounded-full bg-violet-500 px-2 py-0.5 text-[10px] font-semibold text-white shadow-md">
                        当前图
                      </div>
                    )}
                    {hasImage ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setPreviewId(card.slot)}
                          className="block h-full w-full"
                          title={`放大查看${card.label}`}
                        >
                          <img
                            src={withAuthToken(thumbUrl)}
                            alt={card.label}
                            className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                          />
                        </button>
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-slate-950/0 opacity-0 transition group-hover:bg-slate-950/30 group-hover:opacity-100">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-xs font-semibold text-slate-800 shadow-lg">
                            <ZoomIn className="h-3.5 w-3.5" />
                            放大查看
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-slate-400">
                        {inProgress ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
                            <span className="text-xs">正在生成图片…</span>
                          </>
                        ) : card.status === 'failed' ? (
                          <>
                            <AlertTriangle className="h-5 w-5 text-rose-400" />
                            <span className="text-xs">本次生成失败</span>
                          </>
                        ) : (
                          <>
                            <ImageOff className="h-5 w-5" />
                            <span className="text-xs">
                              {card.status === 'draft'
                                ? '文案已就绪，点击生成图片'
                                : card.status === 'waiting_dependency'
                                  ? '等待上游卡片完成…'
                                  : '还没有卡片'}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* 候选图折叠区 */}
                  {(() => {
                    const visibleCandidates = candidates.filter(
                      (c) => (c.thumbnailUrl ?? c.imageUrl) || c.status !== 'failed'
                    );
                    if (visibleCandidates.length === 0) return null;
                    const isExpanded = expandedSlots.has(card.slot);
                    return (
                      <div className="border-t border-slate-100">
                        <button
                          type="button"
                          onClick={() => {
                            setExpandedSlots((prev) => {
                              const next = new Set(prev);
                              if (next.has(card.slot)) next.delete(card.slot);
                              else next.add(card.slot);
                              return next;
                            });
                          }}
                          className="flex w-full items-center gap-1.5 px-3 py-2 text-[11px] font-medium text-slate-500 transition hover:bg-slate-50"
                        >
                          <ChevronDown
                            className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
                          />
                          候选图（{visibleCandidates.length}）
                        </button>
                        {isExpanded && (
                          <div className="px-3 pb-3">
                            <div className="grid grid-cols-4 gap-1.5">
                              {visibleCandidates.map((c) => {
                                const cThumb = c.thumbnailUrl ?? c.imageUrl;
                                return (
                                  <div
                                    key={c.imageJobId}
                                    className="group/c relative aspect-square overflow-hidden rounded-xl bg-slate-100 ring-1 ring-slate-200 transition hover:ring-violet-300"
                                  >
                                    {c.status === 'completed' && cThumb ? (
                                      <img
                                        src={withAuthToken(cThumb)}
                                        alt=""
                                        className="h-full w-full object-cover transition duration-300 group-hover/c:scale-[1.04]"
                                        loading="lazy"
                                      />
                                    ) : c.status === 'failed' ? (
                                      <div className="flex h-full w-full items-center justify-center bg-rose-50 text-xs text-rose-500">
                                        失败
                                      </div>
                                    ) : (
                                      <div className="flex h-full w-full items-center justify-center">
                                        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                                      </div>
                                    )}
                                    {c.status === 'completed' && (
                                      <div className="absolute inset-0 flex items-center justify-center gap-1.5 bg-slate-950/45 opacity-0 transition group-hover/c:opacity-100">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setCandidatePreviewSlot(card.slot);
                                            setCandidatePreviewId(c.imageJobId);
                                          }}
                                          className="rounded-full bg-white p-1.5 shadow-md transition hover:bg-violet-50"
                                          title="放大查看"
                                        >
                                          <ZoomIn className="h-3.5 w-3.5 text-slate-700" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => setPendingAction({ type: 'lock', slot: card.slot, imageJobId: c.imageJobId })}
                                          disabled={videoActive}
                                          className="rounded-full bg-white p-1.5 shadow-md transition hover:bg-violet-50 disabled:opacity-40"
                                          title="锁定为当前图"
                                        >
                                          <Lock className="h-3.5 w-3.5 text-violet-600" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => setPendingAction({ type: 'delete', slot: card.slot, imageJobId: c.imageJobId })}
                                          disabled={videoActive}
                                          className="rounded-full bg-white p-1.5 shadow-md transition hover:bg-rose-50 disabled:opacity-40"
                                          title="删除候选"
                                        >
                                          <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              {/* 生成候选 */}
                              <button
                                type="button"
                                onClick={() => handleClickGenerateImage(card.slot)}
                                disabled={isImageBusy || !card.prompt || videoActive}
                                className="flex aspect-square flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-slate-200 bg-white text-slate-400 transition hover:border-violet-300 hover:bg-violet-50 hover:text-violet-600 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                {isImageBusy ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Plus className="h-4 w-4" />
                                )}
                                <span className="text-[10px]">生成候选</span>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <div className="space-y-2 px-3 py-2">
                    {editingSlot === card.slot ? (
                      <div className="space-y-1.5">
                        <textarea
                          value={editingPrompt}
                          onChange={(e) => setEditingPrompt(e.target.value)}
                          disabled={savingPrompt}
                          className="min-h-[4rem] w-full rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-[12px] leading-5 text-slate-700 outline-none transition focus:border-violet-300 focus:ring-2 focus:ring-violet-100 disabled:opacity-50"
                          placeholder="输入提示词..."
                        />
                        <div className="flex gap-1.5">
                          <button
                            type="button"
                            disabled={savingPrompt}
                            onClick={async () => {
                              setSavingPrompt(true);
                              try {
                                await onSavePrompt(card.slot, editingPrompt);
                                setEditingSlot(null);
                                setEditingPrompt('');
                              } finally {
                                setSavingPrompt(false);
                              }
                            }}
                            className="inline-flex flex-1 items-center justify-center gap-1 rounded-2xl bg-violet-600 px-2 py-1 text-[11px] font-semibold text-white transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {savingPrompt ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                            保存
                          </button>
                          <button
                            type="button"
                            disabled={savingPrompt}
                            onClick={() => {
                              setEditingSlot(null);
                              setEditingPrompt('');
                            }}
                            className="inline-flex flex-1 items-center justify-center gap-1 rounded-2xl border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            取消
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="group/t relative min-h-[3rem] rounded-xl bg-slate-50 px-2.5 py-1.5">
                        {taskRunning &&
                        task?.targetSlot === card.slot &&
                        task?.streamingPartial?.[card.slot] ? (
                          <div className="space-y-1">
                            <div className="flex items-center gap-1 text-[11px] text-violet-600">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              <span>AI 正在生成提示词…</span>
                            </div>
                            <p className="whitespace-pre-wrap text-[12px] leading-5 text-slate-600">
                              {task.streamingPartial[card.slot]}
                              <span className="inline-block h-3 w-0.5 animate-pulse bg-violet-400 align-middle" />
                            </p>
                          </div>
                        ) : card.prompt ? (
                          <>
                            <p className="line-clamp-3 pr-7 text-[12px] leading-5 text-slate-600">
                              {card.prompt}
                            </p>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingSlot(card.slot);
                                setEditingPrompt(card.prompt ?? '');
                              }}
                              disabled={isPromptBusy}
                              className="absolute right-1.5 top-1.5 rounded-lg bg-white/80 p-1 text-slate-400 opacity-0 shadow-sm ring-1 ring-slate-200 transition hover:bg-violet-50 hover:text-violet-600 hover:ring-violet-200 group-hover/t:opacity-100 disabled:cursor-not-allowed disabled:opacity-0"
                              title="编辑提示词"
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                          </>
                        ) : (
                          <p className="text-[12px] leading-5 text-slate-400">
                            生成完成后，这里会显示该卡片实际使用的提示词。
                          </p>
                        )}
                      </div>
                    )}

                    {/* 注入资产 */}
                    {(injectedAssets.characters.length > 0 ||
                      injectedAssets.props.length > 0 ||
                      injectedAssets.scene) && (
                      <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-2.5 py-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[11px] font-medium text-slate-500">当前注入资产</div>
                          {referenceAssetsLoading && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-violet-600">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              核对参考图中
                            </span>
                          )}
                        </div>
                        {referenceAssets ? (
                          <div className="mt-1.5 space-y-1.5">
                            <div>
                              <div className="text-[11px] font-medium text-emerald-700">
                                本次会作为参考图
                              </div>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {referenceAssets.actual.characters.map((item) => (
                                  <span
                                    key={`actual-char-${card.slot}-${item.id}`}
                                    className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700"
                                  >
                                    <span className="rounded-full bg-sky-500 px-1 text-[10px] font-semibold text-white">
                                      {item.referenceIndex}
                                    </span>
                                    {item.name}
                                  </span>
                                ))}
                                {referenceAssets.actual.props.map((item) => (
                                  <span
                                    key={`actual-prop-${card.slot}-${item.id}`}
                                    className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700"
                                  >
                                    <span className="rounded-full bg-amber-500 px-1 text-[10px] font-semibold text-white">
                                      {item.referenceIndex}
                                    </span>
                                    {item.name}
                                  </span>
                                ))}
                                {referenceAssets.actual.scene && (
                                  <span
                                    key={`actual-scene-${card.slot}-${referenceAssets.actual.scene.id}`}
                                    className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
                                  >
                                    <span className="rounded-full bg-emerald-500 px-1 text-[10px] font-semibold text-white">
                                      {referenceAssets.actual.scene.referenceIndex}
                                    </span>
                                    {referenceAssets.actual.scene.name}
                                  </span>
                                )}
                                {referenceAssets.actual.totalCount === 0 && (
                                  <span className="text-[11px] text-slate-400">当前没有可用参考图</span>
                                )}
                              </div>
                            </div>
                            {(referenceAssets.missing.characters.length > 0 ||
                              referenceAssets.missing.props.length > 0 ||
                              referenceAssets.missing.scene) && (
                              <div>
                                <div className="text-[11px] font-medium text-amber-700">
                                  仅文本注入，未进入参考图
                                </div>
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {referenceAssets.missing.characters.map((item) => (
                                    <span
                                      key={`missing-char-${card.slot}-${item.id}`}
                                      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-600"
                                    >
                                      {item.name}
                                      <span className="text-slate-400">· {missingReasonLabel(item.reason)}</span>
                                    </span>
                                  ))}
                                  {referenceAssets.missing.props.map((item) => (
                                    <span
                                      key={`missing-prop-${card.slot}-${item.id}`}
                                      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-600"
                                    >
                                      {item.name}
                                      <span className="text-slate-400">· {missingReasonLabel(item.reason)}</span>
                                    </span>
                                  ))}
                                  {referenceAssets.missing.scene && (
                                    <span
                                      key={`missing-scene-${card.slot}-${referenceAssets.missing.scene.id}`}
                                      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-600"
                                    >
                                      {referenceAssets.missing.scene.name}
                                      <span className="text-slate-400">
                                        · {missingReasonLabel(referenceAssets.missing.scene.reason)}
                                      </span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <>
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {injectedAssets.characters.map((name) => (
                                <span
                                  key={`fallback-char-${card.slot}-${name}`}
                                  className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700"
                                >
                                  {name}
                                </span>
                              ))}
                              {injectedAssets.props.map((name) => (
                                <span
                                  key={`fallback-prop-${card.slot}-${name}`}
                                  className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700"
                                >
                                  {name}
                                </span>
                              ))}
                              {injectedAssets.scene && (
                                <span
                                  key={`fallback-scene-${card.slot}-${injectedAssets.scene}`}
                                  className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
                                >
                                  {injectedAssets.scene}
                                </span>
                              )}
                            </div>
                            <p className="mt-1.5 text-[11px] leading-5 text-slate-500">
                              正在核对这些 `@` 资产里哪些真的有可用参考图。
                            </p>
                          </>
                        )}
                      </div>
                    )}

                    {/* 操作按钮 */}
                    <div className="space-y-1.5">
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleClickGeneratePrompt(card.slot)}
                          disabled={isPromptBusy}
                          className="inline-flex flex-1 items-center justify-center gap-1 rounded-2xl border border-violet-200 bg-violet-50 px-2 py-1.5 text-xs font-semibold text-violet-700 transition hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isPromptBusy ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Wand2 className="h-3 w-3" />
                          )}
                          {card.prompt ? '重写文案' : '文案生成'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleClickGenerateImage(card.slot)}
                          disabled={isImageBusy || !card.prompt}
                          className="inline-flex flex-1 items-center justify-center gap-1 rounded-2xl border border-sky-200 bg-sky-50 px-2 py-1.5 text-xs font-semibold text-sky-700 transition hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isImageBusy ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Images className="h-3 w-3" />
                          )}
                          {card.imageJobId ? '重新生成图' : '图片生成'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setLibraryImportSlot(card.slot)}
                          disabled={videoActive}
                          className="inline-flex items-center justify-center gap-1 rounded-2xl border border-slate-200 bg-white px-2 py-1.5 text-xs font-medium text-slate-600 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-40"
                          title="从图库导入"
                        >
                          <FolderOpen className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 文案生成弹窗 */}
      <TrinityPromptGenerateDialog
        open={!!promptDialogSlot}
        slot={promptDialogSlot ?? 'role'}
        cardLabel={promptDialogCard?.label ?? ''}
        hasExistingPrompt={!!promptDialogCard?.prompt}
        existingPrompt={promptDialogCard?.prompt ?? null}
        aiCost={aiCost}
        aiEnabled={aiEnabled}
        task={task}
        onConfirm={handleConfirmPrompt}
        onSave={handleSavePromptFromDialog}
        onClose={() => setPromptDialogSlot(null)}
      />

      {/* 图片生成弹窗 */}
      <TrinityImageGenerateDialog
        open={!!imageDialogSlot}
        slot={imageDialogSlot ?? 'role'}
        cardLabel={imageDialogCard?.label ?? ''}
        prompt={imageDialogCard?.prompt ?? null}
        referenceCount={imagePreview?.referenceCount ?? 0}
        estimatedCredits={imagePreview?.estimatedCredits ?? 0}
        currentCredits={currentCredits}
        models={models}
        defaultModelId={models[0]?.id ?? ''}
        defaultQuality="low"
        onConfirm={handleConfirmImage}
        onClose={() => {
          setImageDialogSlot(null);
          setImagePreview(null);
        }}
      />

      {/* 图片灯箱（主卡片） */}
      {(() => {
        const currentIndex = previewId
          ? lightboxItems.findIndex((item) => item.id === previewId)
          : -1;
        if (currentIndex < 0) return null;
        return (
          <FrameLightbox
            items={lightboxItems}
            currentIndex={currentIndex}
            onIndexChange={(i) => setPreviewId(lightboxItems[i]?.id ?? null)}
            onClose={() => setPreviewId(null)}
            buildActions={() => []}
          />
        );
      })()}

      {/* 候选图灯箱 */}
      {candidatePreviewSlot && (() => {
        const items = candidateLightboxItems.get(candidatePreviewSlot);
        if (!items?.length) return null;
        const idx = candidatePreviewId
          ? items.findIndex((it) => it.id === candidatePreviewId)
          : 0;
        const currentIndex = idx >= 0 ? idx : 0;
        return (
          <FrameLightbox
            items={items}
            currentIndex={currentIndex}
            onIndexChange={(i) => setCandidatePreviewId(items[i]?.id ?? null)}
            onClose={() => {
              setCandidatePreviewSlot(null);
              setCandidatePreviewId(null);
            }}
            buildActions={(item) => [
              {
                key: 'lock-candidate',
                label: '锁定为当前图',
                icon: <Lock className="h-4 w-4" />,
                onClick: () => {
                  setPendingAction({ type: 'lock', slot: candidatePreviewSlot, imageJobId: item.id });
                  setCandidatePreviewSlot(null);
                },
                disabled: videoActive,
              },
              {
                key: 'delete-candidate',
                label: '删除候选',
                icon: <Trash2 className="h-4 w-4" />,
                onClick: () => setPendingAction({ type: 'delete', slot: candidatePreviewSlot, imageJobId: item.id }),
                variant: 'danger' as const,
                disabled: videoActive,
              },
            ]}
          />
        );
      })()}

      {/* 确认弹窗（替代 window.confirm） */}
      {pendingAction && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-slate-900">
              {pendingAction.type === 'lock' ? '锁定为当前图' : '删除候选图'}
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              {pendingAction.type === 'lock'
                ? '确定将此候选图锁定为当前图吗？'
                : '确定删除此候选图吗？此操作不可撤销。'}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingAction(null)}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => {
                  const { type, slot, imageJobId } = pendingAction;
                  setPendingAction(null);
                  if (type === 'lock') onLockCandidate(slot, imageJobId);
                  else onDeleteCandidate(slot, imageJobId);
                }}
                className={`rounded-xl px-4 py-2 text-sm font-semibold text-white transition ${
                  pendingAction.type === 'lock'
                    ? 'bg-violet-600 hover:bg-violet-500'
                    : 'bg-rose-600 hover:bg-rose-500'
                }`}
              >
                {pendingAction.type === 'lock' ? '确认锁定' : '确认删除'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 从图库导入弹窗 */}
      <ImportFromLibraryDialog
        open={!!libraryImportSlot}
        target={
          libraryImportSlot
            ? {
                type: 'trinitySlot',
                shotId,
                slot: libraryImportSlot,
                targetLabel: getCardBySlot(cards, libraryImportSlot)?.label,
              }
            : ({ type: 'shot', shotId: '' } as LibraryImportTarget)
        }
        onClose={() => setLibraryImportSlot(null)}
        onImported={() => {
          onRefresh();
        }}
      />
    </div>,
    document.body
  );
}
